import clsx from 'clsx'
import { FC, PropsWithChildren, useMemo } from 'react'
import { HeaderProps } from 'react-table'
import { initialQueryState } from '../../../../../../../_metronic/helpers'
import { useQueryRequest } from '../../core/QueryRequestProvider'
import { User } from '../../core/_models'
import { COLUMN_ICON_MAP, IconConfig } from './ColumnHeaderIcons'

type Props = {
  className?: string
  title?: string
  tableProps: PropsWithChildren<HeaderProps<User>>
}


const ColumnIconBadge: FC<{ config: IconConfig }> = ({ config }) => (
  <div
    style={{
      width: '14px',
      height: '14px',
      borderRadius: '50%',
      backgroundColor: config.badgeBg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}
  >
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='10'
      height='10'
      viewBox='0 0 24 24'
      fill='none'
      stroke={config.stroke}
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      {config.paths}
    </svg>
  </div>
)

// ─────────────────────────────────────────────────────────────────────────────
// UserCustomHeader
// ─────────────────────────────────────────────────────────────────────────────
const UserCustomHeader: FC<Props> = ({ className, title, tableProps }) => {
  const id = tableProps.column.id
  const { state, updateState } = useQueryRequest()

  const isSelectedForSorting = useMemo(() => {
    return state.sort && state.sort === id
  }, [state, id])
  const order: 'asc' | 'desc' | undefined = useMemo(() => state.order, [state])

  const sortColumn = () => {
    // avoid sorting for these columns
    if (id === 'actions' || id === 'selection') {
      return
    }

    if (!isSelectedForSorting) {
      // enable sort asc
      updateState({ sort: id, order: 'asc', ...initialQueryState })
      return
    }

    if (isSelectedForSorting && order !== undefined) {
      if (order === 'asc') {
        // enable sort desc
        updateState({ sort: id, order: 'desc', ...initialQueryState })
        return
      }

      // disable sort
      updateState({ sort: undefined, order: undefined, ...initialQueryState })
    }
  }

  const isSortable = false && (id !== 'actions' && id !== 'selection')
  const iconConfig = title ? COLUMN_ICON_MAP[title] : undefined

  return (
    <th
      {...tableProps.column.getHeaderProps()}
      className={clsx(
        className,
        isSelectedForSorting && order !== undefined && `table-sort-${order}`
      )}
      style={{
        cursor: isSortable ? 'pointer' : 'default',
        backgroundColor: '#ffffff',
        color: '#3d4a5c',
        fontWeight: 700,
        fontSize: '11px',
        textTransform: 'uppercase',
        letterSpacing: '0.6px',
        padding: '14px 12px',
        borderBottom: '2px solid #e2e8f0',
        whiteSpace: 'nowrap',
        verticalAlign: 'middle',
        userSelect: 'none',
      }}
      onClick={sortColumn}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
        {iconConfig ? <ColumnIconBadge config={iconConfig} /> : '#'}
        {title}
        {isSortable && (
          <span style={{ display: 'inline-flex', flexDirection: 'column', marginLeft: '4px', opacity: 0.4, lineHeight: 1 }}>
            <span
              style={{
                fontSize: '8px',
                color: isSelectedForSorting && order === 'asc' ? '#4361ee' : '#6b7280',
                opacity: isSelectedForSorting && order === 'asc' ? 1 : 0.5,
              }}
            >
              ▲
            </span>
            <span
              style={{
                fontSize: '8px',
                color: isSelectedForSorting && order === 'desc' ? '#4361ee' : '#6b7280',
                opacity: isSelectedForSorting && order === 'desc' ? 1 : 0.5,
              }}
            >
              ▼
            </span>
          </span>
        )}
      </span>
    </th>
  )
}

export { UserCustomHeader }
